import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { getGuideById } from "../../data/guides";

const SherpaGuideDetail = () => {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const guide = getGuideById(guideId);

  if (!guide) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Icon name="Mountain" size={64} className="text-muted-foreground mx-auto mb-6" />
          <h1 className="text-3xl font-headline font-bold text-foreground mb-4">Guide Not Found</h1>
          <p className="text-text-secondary mb-8">The guide you're looking for doesn't exist or may have been removed.</p>
          <Button variant="default" onClick={() => navigate("/sherpa-guide-profiles")}>
            Back to All Guides
          </Button>
        </main>
      </div>
    );
  }

  const isTrekOfficer = !guide.summits || guide.summits === 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate("/sherpa-guide-profiles")}
            className="flex items-center gap-2 text-text-secondary hover:text-foreground transition-colors mb-8 group"
          >
            <Icon name="ArrowLeft" size={18} />
            <span className="text-sm font-medium">Back to All Guides</span>
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-72 flex-shrink-0">
              <div className="flex flex-col items-center lg:items-stretch gap-6">
                <div className="w-40 h-40 lg:w-56 lg:h-56 rounded-full overflow-hidden border-4 border-primary/20 mx-auto lg:mx-0 shadow-lg">
                  <img
                    src={guide.image}
                    alt={guide.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = "/assets/images/no_image.png"; }}
                  />
                </div>

                {guide.certifications?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider text-center lg:text-left">
                      Certifications
                    </h4>
                    {guide.certifications.map((cert, i) => (
                      <div key={i} className="w-full rounded-lg overflow-hidden border-2 border-primary/10 mx-auto lg:mx-0 shadow-md">
                        <img
                          src={cert}
                          alt={`Certificate ${i + 1}`}
                          className="w-full h-auto object-contain"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="bg-card rounded-2xl shadow-elevation p-6 md:p-8 space-y-8">
                <div>
                  <h1 className="text-3xl md:text-4xl font-headline font-bold text-foreground">
                    {guide.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <span className="flex items-center gap-1.5 text-text-secondary">
                      <Icon name="MapPin" size={16} />
                      <span className="text-sm">{guide.village}</span>
                    </span>
                    <span className="text-text-secondary/40">|</span>
                    <span className="text-sm font-medium text-primary">
                      {isTrekOfficer ? "Trek Officer" : "Sherpa Guide"}
                    </span>
                  </div>
                </div>

                <hr className="border-border" />

                <div>
                  <h2 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="User" size={18} className="text-primary" />
                    About
                  </h2>
                  <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                    {guide.story}
                  </p>
                </div>

                <hr className="border-border" />

                {guide.mountains?.length > 0 && (
                  <>
                    <div>
                      <h2 className="text-lg font-headline font-bold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="Mountain" size={18} className="text-primary" />
                        Mountaineering Experience
                      </h2>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-3 font-semibold text-foreground">Mountain</th>
                              <th className="text-right py-2 px-3 font-semibold text-foreground">Elevation</th>
                              <th className="text-right py-2 px-3 font-semibold text-foreground">Summits</th>
                            </tr>
                          </thead>
                          <tbody>
                            {guide.mountains.map((m, i) => (
                              <tr key={i} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                                <td className="py-2.5 px-3 text-foreground font-medium">{m.name}</td>
                                <td className="py-2.5 px-3 text-right text-text-secondary">{m.elevation}</td>
                                <td className="py-2.5 px-3 text-right">
                                  <span className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                    {m.summits}
                                    <Icon name="Mountain" size={11} />
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <hr className="border-border" />
                  </>
                )}

                <hr className="border-border" />

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="default"
                    className="btn-conversion flex-1"
                    iconName="Calendar"
                    iconPosition="left"
                    size="lg"
                    onClick={() => navigate("/booking-consultation-center")}
                  >
                    Book Consultation with {guide.name.split(" ")[0]}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    iconName="MessageCircle"
                    iconPosition="left"
                    size="lg"
                    onClick={() => navigate("/booking-consultation-center")}
                  >
                    Send Message
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SherpaGuideDetail;
